/**
 * Signature Move Effects Renderer
 * 
 * Handles rendering of signature move visual effects:
 * - Tile-based AOE radius indicator
 * - Laser beam from caster to target
 * - Pulsing damage animation over 3 seconds
 * - Wave expansion effect
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../types';
import { drawGridRadiusCircular } from '../utils/RadiusRenderer';

export interface SignatureMoveEffectData {
  casterX: number;
  casterY: number;
  targetX: number;
  targetY: number;
  aoeRadiusTiles: number;
  color: number;
  element: string;
  moveId: string;
}

export class SignatureMoveEffectsRenderer {
  private scene: Phaser.Scene;
  private activeEffects: Map<string, Phaser.GameObjects.Container> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Show complete signature move effect:
   * 1. Laser beam from caster to target
   * 2. AOE radius indicator at target
   * 3. Pulsing damage animation over 3 seconds
   */
  public showSignatureMoveEffect(data: SignatureMoveEffectData): void {
    const effectId = `${data.moveId}_${Date.now()}`;
    
    // Create container for all effects
    const container = this.scene.add.container(0, 0);
    container.setDepth(199);
    this.activeEffects.set(effectId, container);

    // 1. Draw laser beam from caster to target
    this.drawLaserBeam(container, data.casterX, data.casterY, data.targetX, data.targetY, data.color);

    // 2. Draw tile-based AOE radius indicator at target
    this.drawAOERadiusIndicator(container, data.targetX, data.targetY, data.aoeRadiusTiles, data.color);

    // 3. Create pulsing damage animation over 3 seconds
    this.createPulsingDamageEffect(container, data.targetX, data.targetY, data.aoeRadiusTiles, data.color);

    // Cleanup after 3.5 seconds (gives extra time for animations to finish)
    this.scene.time.delayedCall(3500, () => {
      if (this.activeEffects.has(effectId)) {
        container.destroy();
        this.activeEffects.delete(effectId);
      }
    });
  }

  /**
   * Draw animated laser beam from caster to target
   */
   private drawLaserBeam(
     container: Phaser.GameObjects.Container,
     fromX: number,
     fromY: number,
     toX: number,
     toY: number,
     color: number
   ): void {
     const beamGraphics = this.scene.add.graphics();
     beamGraphics.setPosition(0, 0);
     beamGraphics.setDepth(199);
     container.add(beamGraphics);

     // Draw main beam (bright core)
     beamGraphics.lineStyle(3, color, 1);
     beamGraphics.beginPath();
     beamGraphics.moveTo(fromX, fromY);
     beamGraphics.lineTo(toX, toY);
     beamGraphics.strokePath();

     // Draw outer glow (semi-transparent)
     beamGraphics.lineStyle(8, color, 0.4);
     beamGraphics.beginPath();
     beamGraphics.moveTo(fromX, fromY);
     beamGraphics.lineTo(toX, toY);
     beamGraphics.strokePath();

     // Animate beam intensity with pulsing effect
     const beamData = { phase: 0 };
     this.scene.tweens.add({
       targets: beamData,
       phase: Math.PI * 4, // Complete 2 full sine cycles
       duration: 1500,
       ease: 'Linear',
       onUpdate: () => {
         const intensity = 0.5 + Math.sin(beamData.phase) * 0.3;
         beamGraphics.setAlpha(intensity);
       },
       onComplete: () => {
         beamGraphics.setAlpha(0);
       },
     });
   }

   /**
    * Draw tile-based AOE radius indicator (shows which tiles will be affected)
    * Uses Euclidean distance to match server-side damage calculation
    */
   private drawAOERadiusIndicator(
     container: Phaser.GameObjects.Container,
     centerX: number,
     centerY: number,
     radiusTiles: number,
     color: number
   ): void {
     const indicatorGraphics = this.scene.add.graphics();
     indicatorGraphics.setPosition(0, 0);
     indicatorGraphics.setDepth(199);
     container.add(indicatorGraphics);

     // Use tile-based radius renderer (circular Euclidean distance)
     // This shows exactly which tiles will take damage
     drawGridRadiusCircular(indicatorGraphics, {
       centerX,
       centerY,
       radiusTiles,
       color,
       alpha: 0.35,
       fadeToEdge: false, // Don't fade, keep consistent visibility
       includeCenter: true,
       ringOnly: false,
     });

     // Add subtle borders to each tile for clarity
     const centerTileX = Math.floor(centerX / TILE_SIZE);
     const centerTileY = Math.floor(centerY / TILE_SIZE);
     indicatorGraphics.lineStyle(1, color, 0.5);
     
     for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
       for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
         const euclideanDist = Math.sqrt(dx * dx + dy * dy);
         if (euclideanDist <= radiusTiles) {
           const worldX = (centerTileX + dx) * TILE_SIZE;
           const worldY = (centerTileY + dy) * TILE_SIZE;
           indicatorGraphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
         }
       }
     }

     // Fade in the indicator
     indicatorGraphics.setAlpha(0);
     this.scene.tweens.add({
       targets: indicatorGraphics,
       alpha: 0.35,
       duration: 300,
       ease: 'Quad.easeOut',
     });

     // Fade out after 2 seconds
     this.scene.time.delayedCall(2000, () => {
       this.scene.tweens.add({
         targets: indicatorGraphics,
         alpha: 0,
         duration: 500,
         ease: 'Quad.easeIn',
       });
     });
   }

  /**
   * Create pulsing damage animation that expands in waves
   * Occupies entire tiles, pulses for 3 seconds, 3 damage ticks (0s, 1s, 2s)
   */
  private createPulsingDamageEffect(
    container: Phaser.GameObjects.Container,
    centerX: number,
    centerY: number,
    radiusTiles: number,
    color: number
  ): void {
    const pulseDuration = 3000; // eslint-disable-line @typescript-eslint/no-unused-vars
    const pulseTiming = [0, 1000, 2000]; // Damage at 0ms, 1s, 2s
    const pulseCount = 3; // Match 3 damage ticks
    
    // Create multiple wave pulses
    for (let p = 0; p < pulseCount; p++) {
      this.scene.time.delayedCall(pulseTiming[p], () => {
        this.createSinglePulseWave(container, centerX, centerY, radiusTiles, color, p);
      });
    }
  }

  /**
   * Create a single pulsing wave expansion
   */
  private createSinglePulseWave(
    container: Phaser.GameObjects.Container,
    centerX: number,
    centerY: number,
    maxRadiusTiles: number,
    color: number,
    pulseIndex: number
  ): void {
    // Create wave expansion graphics
    const waveGraphics = this.scene.add.graphics();
    container.add(waveGraphics);

    // Animation: wave expands from center to full radius over 800ms, then fades
    let currentRadius = 0; // eslint-disable-line @typescript-eslint/no-unused-vars
    
    const startTime = Date.now();
    const expansionDuration = 800; // Expand over 800ms
    const fadeDuration = 200; // Fade over 200ms
    const totalDuration = expansionDuration + fadeDuration;

    const updateWave = () => {
      const elapsed = Date.now() - startTime;
      
      if (elapsed > totalDuration) {
        waveGraphics.destroy();
        return;
      }

      waveGraphics.clear();
      
      // Calculate current radius and alpha
      let radiusMultiplier: number;
      let alpha: number;

      if (elapsed < expansionDuration) {
        // Expansion phase: grow from 0 to full radius
        radiusMultiplier = elapsed / expansionDuration;
        alpha = 0.6; // Bright during expansion
      } else {
        // Fade phase: maintain radius but reduce alpha
        radiusMultiplier = 1.0;
        const fadeElapsed = elapsed - expansionDuration;
        alpha = 0.6 * (1 - fadeElapsed / fadeDuration);
      }

      const currentRadiusTiles = maxRadiusTiles * radiusMultiplier;

      // Draw all tiles in the expanding radius
      this.drawTileGrid(waveGraphics, centerX, centerY, currentRadiusTiles, color, alpha);
    };

    // Animate wave expansion and fade using tween
    const animData = { elapsed: 0 };
    this.scene.tweens.add({
      targets: animData,
      elapsed: totalDuration,
      duration: totalDuration,
      ease: 'Linear',
      onUpdate: () => {
        updateWave();
      },
      onComplete: () => {
        waveGraphics.destroy();
      },
    });
  }

  /**
   * Draw grid of tiles in a circular radius
   */
  private drawTileGrid(
    graphics: Phaser.GameObjects.Graphics,
    centerX: number,
    centerY: number,
    radiusTiles: number,
    color: number,
    alpha: number
  ): void {
    // Convert world coords to tile coords
    const centerTileX = Math.floor(centerX / TILE_SIZE);
    const centerTileY = Math.floor(centerY / TILE_SIZE);

    // Iterate over all tiles in bounding box
    for (let dy = -Math.ceil(radiusTiles); dy <= Math.ceil(radiusTiles); dy++) {
      for (let dx = -Math.ceil(radiusTiles); dx <= Math.ceil(radiusTiles); dx++) {
        // Calculate Euclidean distance from center
        const euclideanDist = Math.sqrt(dx * dx + dy * dy);
        
        // Skip tiles outside radius
        if (euclideanDist > radiusTiles) {
          continue;
        }

        // Calculate world position
        const worldX = (centerTileX + dx) * TILE_SIZE;
        const worldY = (centerTileY + dy) * TILE_SIZE;

        // Draw tile with pulsing effect
        graphics.fillStyle(color, alpha);
        graphics.fillRect(worldX, worldY, TILE_SIZE, TILE_SIZE);

        // Add subtle border for definition
        graphics.lineStyle(1, color, alpha * 0.5);
        graphics.strokeRect(worldX, worldY, TILE_SIZE, TILE_SIZE);
      }
    }
  }

  /**
   * Clean up all active effects
   */
  public destroy(): void {
    this.activeEffects.forEach(container => {
      container.destroy();
    });
    this.activeEffects.clear();
  }
}
